import { NotificationService } from '@optsolservices/ngx-notification';
import { Subscription } from 'rxjs';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { FieldConfig, ValidatorConfig } from "./models/dynamic-form-field";
import { checkFilesValidationErrors } from './custom-validators/check-files-validation-errors';
import { DynamicFileSelectComponent } from './dynamic-file-select/dynamic-file-select.component';
import jsSHA from 'jssha';
import * as CryptoJS from "crypto-js";

@Component({
  selector: 'optsol-ngx-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss']
})
export class DynamicFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren(DynamicFileSelectComponent) dynamicFileSelectComponents: QueryList<DynamicFileSelectComponent>;

  @Input() set fields(value: FieldConfig[]) {
    if (!value) {
      this.formFields = [];
      return;
    }

    this.formFields = value.map(m => {
      return Object.assign(m, {
        validations: m.validations ? m.validations.map(v => Object.assign(v, { validator: this.validatorFactory(v) })) : []
      })
    });
  };
  @Input() initialValue: any;
  @Input() fxLayout: string;
  @Input() fxLayoutAlign: string;
  @Input() fxLayoutGap: string;
  @Output() submit: EventEmitter<any> = new EventEmitter<any>();

  form: FormGroup;
  formFields: FieldConfig[] = null;

  formControlSubscriptions: {
    parentField: string;
    childField: string;
    subscription: Subscription;
  }[] = [];

  public get errors() {
    if (this.form) {
      const errors = [];
      this.formFields.forEach(field => {
        const control = this.form.get(field.name);
        errors.push({
          field: field.name,
          errors: control.errors
        });
      });
      return errors;
    } else {
      return [];
    }
  }

  public get value() {
    const formValue = this.form.getRawValue();
      this.formFields.forEach(field => {
        if (field.additionalOptions && field.additionalOptions.hashingAlgorithm) {
          const plainValue = formValue[field.name];
          let hashedValue = formValue[field.name];

          switch (field.additionalOptions.hashingAlgorithm) {
            case 'MD5':
              hashedValue = CryptoJS.MD5(plainValue).toString();
              break;

            case 'SHA1':
              const sha1 = new jsSHA('SHA-1', 'TEXT', { encoding: 'UTF8' });
              sha1.update(plainValue);
              hashedValue = sha1.getHash('HEX');
              break;

            case 'SHA2_256':
              const sha256 = new jsSHA('SHA-256', 'TEXT', { encoding: 'UTF8' });
              sha256.update(plainValue);
              hashedValue = sha256.getHash('HEX');
              break;

            case 'SHA2_512':
              const sha512 = new jsSHA('SHA-512', 'TEXT', { encoding: 'UTF8' });
              sha512.update(plainValue);
              hashedValue = sha512.getHash('HEX');
              break;
          }

          formValue[field.name] = hashedValue;
        }
      });

      return formValue;
  }

  public get valid() {
    return this.form.valid;
  }

  constructor(
    private fb: FormBuilder,
    private notificationService: NotificationService) { }

  ngOnInit() {
    this.form = this.createControl();

    this.formFields.forEach(field => {
      if (field.dependsOn) {
        if (this.formControlSubscriptions.findIndex(f => f.parentField === field.dependsOn && f.childField === field.name) === -1) {
          this.formControlSubscriptions.push({
            parentField: field.dependsOn,
            childField: field.name,
            subscription: this.form.controls[field.dependsOn].valueChanges.subscribe(value => {
              this.rebuildCascadingOptionsForField(field, field.dependsOn, this.value);
            })
          });
        }
      }
    });

    // this.form.valueChanges.subscribe(value => {
    //   this.rebuildCascadingOptions(this.value);
    // });
  }

  ngOnDestroy(): void {
    this.formControlSubscriptions.forEach(s => s.subscription.unsubscribe());
  }

  ngAfterViewInit(): void {
    if (this.form && this.initialValue) {
      this.form.patchValue(this.initialValue);
      // this.validateAllFormFields(this.form);
    }
  }

  onSubmit(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.form.valid) {
      const formValue = this.form.getRawValue();
      this.formFields.forEach(field => {
        if (field.additionalOptions && field.additionalOptions.hashingAlgorithm) {
          const plainValue = formValue[field.name];
          let hashedValue = formValue[field.name];

          switch (field.additionalOptions.hashingAlgorithm) {
            case 'MD5':
              hashedValue = CryptoJS.MD5(plainValue);
              break;

            case 'SHA1':
              const sha1 = new jsSHA('SHA-1', 'TEXT', { encoding: 'UTF8' });
              sha1.update(plainValue);
              hashedValue = sha1.getHash('HEX');
              break;

            case 'SHA2_256':
              const sha256 = new jsSHA('SHA-256', 'TEXT', { encoding: 'UTF8' });
              sha256.update(plainValue);
              hashedValue = sha256.getHash('HEX');
              break;

            case 'SHA2_512':
              const sha512 = new jsSHA('SHA-512', 'TEXT', { encoding: 'UTF8' });
              sha512.update(plainValue);
              hashedValue = sha512.getHash('HEX');
              break;
          }

          formValue[field.name] = hashedValue;
        }
      });

      this.submit.emit(formValue);
    } else {
      this.validateAllFormFields(this.form);
    }
  }

  rebuildCascadingOptionsForField(formField: FieldConfig, dependsOn: string, formValue: any) {
    const control = this.form.get(formField.name);
    const dependantControlValue = formValue[dependsOn];
    const controlValue = control.value;

    if (dependantControlValue) {
      const cascadingOptions = formField.cascadingOptions || [];
      const newOptions = cascadingOptions.find(f => f.value === dependantControlValue);
      formField.options = newOptions?.options || [];

      if (controlValue) {
        if (formField.options.findIndex(f => f.value === controlValue) === -1) {
          control.reset();
        }
      }
    } else {
      formField.options = [];
      if (controlValue) {
        control.reset();
      }
    }
  }

  createControl() {
    const group = this.fb.group({});
    this.formFields.forEach(field => {
      if (field.type === "button") return;
      const control = this.fb.control(
        field.value ? field.value : (field.type === "checkbox" ? false : null),
        this.bindValidations(field.validations || [])
      );
      if (field.readOnly === true) {
        control.disable();
      }
      group.addControl(field.name, control);
    });
    return group;
  }

  bindValidations(validations: any) {
    if (validations.length > 0) {
      const validList = [];
      validations.forEach(valid => {
        validList.push(valid.validator);
      });
      return Validators.compose(validList);
    }
    return null;
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control.markAsTouched({ onlySelf: true });
    });
  }

  validatorFactory(validator: ValidatorConfig) {
    /* https://angular.io/api/forms/Validators */
    switch (validator.name) {
      case "min":
        return Validators.min(validator.min);
      case "max":
        return Validators.max(validator.max);
      case "required":
        return Validators.required;
      case "requiredTrue":
        return Validators.requiredTrue;
      case "email":
        return Validators.email;
      case "minlength":
        return Validators.minLength(validator.min);
      case "maxlength":
        return Validators.maxLength(validator.max);
      case "pattern":
        return Validators.pattern(validator.regEx);
      case "nullValidator":
        return Validators.nullValidator;
      case "checkFiles":
        return checkFilesValidationErrors();
      default:
        this.notificationService.error(`validatorFactory: Unknown validator: ${validator.name}. Fallback to default required validator`);
        console.error('validatorFactory: Fallback to default validator', validator);
        return Validators.required;
    }
  }

  reset() {
    this.form.reset();
    if (this.dynamicFileSelectComponents) {
      this.dynamicFileSelectComponents.forEach(dynamicFileSelectComponent => {
        dynamicFileSelectComponent.reset();
      });
    }
  }
}
